// Landing page performance optimization utilities
export class PreloadOptimizer {
  constructor() {
    this.preloadedResources = new Set();
    this.criticalResources = new Map();
    this.performanceObserver = null;
  }

  // Preload critical resources for landing page
  preloadCriticalResources() {
    const criticalResources = [
      // Critical images
      { href: "/assets/og-image.jpeg", as: "image", priority: "high" },
      { href: "/hero-banner.jpg", as: "image", priority: "high" },

      // Critical fonts - use CSS file that imports from Google Fonts
      { href: "/fonts/space-grotesk.css", as: "style", priority: "high" },
    ];

    criticalResources.forEach((resource) => {
      this.preloadResource(resource);
    });
  }

  // Preload resource with performance monitoring
  preloadResource({ href, as, priority = "low", crossorigin = false }) {
    if (this.preloadedResources.has(href)) return;

    const link = document.createElement("link");
    link.rel = "preload";
    link.href = href;
    link.as = as;

    if (priority === "high") {
      link.fetchPriority = "high";
    }

    if (crossorigin) {
      link.crossOrigin = "anonymous";
    }

    // Monitor preload performance
    link.onload = () => {
      this.criticalResources.set(href, {
        status: "loaded",
        loadTime: performance.now(),
        size: this.getResourceSize(href),
      });
    };

    link.onerror = () => {
      this.criticalResources.set(href, {
        status: "error",
        loadTime: performance.now(),
      });
    };

    document.head.appendChild(link);
    this.preloadedResources.add(href);
  }

  // Preload next sections based on user behavior
  preloadNextSections() {
    const sectionsToPreload = [
      () => import("@/pages/landing/components/ProductHighlightSection"),
      () => import("@/pages/landing/components/WorkflowSection"),
      () => import("@/pages/landing/components/PricingSection"),
    ];

    // Preload with a delay to not block initial rendering
    setTimeout(() => {
      sectionsToPreload.forEach((importFn, index) => {
        setTimeout(() => {
          importFn().catch((err) => {
            console.warn(`Failed to preload section ${index}:`, err);
          });
        }, index * 500); // Stagger preloading
      });
    }, 1000);
  }

  // Intelligent preloading based on user interaction
  enableIntelligentPreloading() {
    // Preload on hover/touch
    const preloadOnInteraction = (selector, importFn) => {
      const elements = document.querySelectorAll(selector);
      elements.forEach((element) => {
        const preloadHandler = () => {
          importFn().catch((err) => console.warn("Preload failed:", err));
          element.removeEventListener("mouseenter", preloadHandler);
          element.removeEventListener("touchstart", preloadHandler);
        };

        element.addEventListener("mouseenter", preloadHandler, { once: true });
        element.addEventListener("touchstart", preloadHandler, { once: true });
      });
    };

    // Preload pricing section when user scrolls past hero
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            import("@/pages/landing/components/PricingSection").catch((err) => {
              console.warn("Failed to preload pricing section:", err);
            });
            observer.disconnect();
          }
        });
      },
      { threshold: 0.5 }
    );

    // Observe hero section bottom
    const heroSection = document.querySelector("section");
    if (heroSection) {
      observer.observe(heroSection);
    }
  }

  // Monitor Core Web Vitals
  monitorCoreWebVitals() {
    if (!("PerformanceObserver" in window)) return;

    // Monitor LCP
    const lcpObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1];

      console.log("LCP:", lastEntry.startTime);

      // If LCP is slow, reduce animations
      if (lastEntry.startTime > 2500) {
        document.documentElement.style.setProperty(
          "--animation-duration",
          "0.1s"
        );
        document.documentElement.classList.add("reduce-motion");
      }
    });

    lcpObserver.observe({ entryTypes: ["largest-contentful-paint"] });

    // Monitor FID
    const fidObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach((entry) => {
        console.log("FID:", entry.processingStart - entry.startTime);
      });
    });

    fidObserver.observe({ entryTypes: ["first-input"] });

    // Monitor CLS
    const clsObserver = new PerformanceObserver((list) => {
      let clsScore = 0;
      const entries = list.getEntries();

      entries.forEach((entry) => {
        if (!entry.hadRecentInput) {
          clsScore += entry.value;
        }
      });

      console.log("CLS:", clsScore);
    });

    clsObserver.observe({ entryTypes: ["layout-shift"] });
  }

  // Get resource size for monitoring
  getResourceSize(href) {
    const entries = performance.getEntriesByName(href);
    return entries.length > 0 ? entries[0].transferSize : 0;
  }

  // Cleanup method
  cleanup() {
    if (this.performanceObserver) {
      this.performanceObserver.disconnect();
    }
    this.preloadedResources.clear();
    this.criticalResources.clear();
  }

  // Get performance report
  getPerformanceReport() {
    return {
      preloadedResources: Array.from(this.preloadedResources),
      criticalResources: Object.fromEntries(this.criticalResources),
      totalPreloaded: this.preloadedResources.size,
    };
  }
}

// Singleton instance
export const preloadOptimizer = new PreloadOptimizer();

// Auto-initialize for landing page
if (typeof window !== "undefined") {
  // Initialize when DOM is ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      preloadOptimizer.preloadCriticalResources();
      preloadOptimizer.monitorCoreWebVitals();
    });
  } else {
    preloadOptimizer.preloadCriticalResources();
    preloadOptimizer.monitorCoreWebVitals();
  }
}

// Export utility functions
export const preloadLandingPageResources = () => {
  preloadOptimizer.preloadCriticalResources();
  preloadOptimizer.preloadNextSections();
  preloadOptimizer.enableIntelligentPreloading();
};

export const optimizeForLandingPage = () => {
  // Reduce JavaScript execution time techniques

  // 1. Defer non-critical JavaScript
  const deferNonCriticalJS = () => {
    const scripts = document.querySelectorAll("script[data-defer]");
    scripts.forEach((script) => {
      setTimeout(() => {
        const newScript = document.createElement("script");
        newScript.src = script.src;
        newScript.async = true;
        document.head.appendChild(newScript);
      }, 100);
    });
  };

  // 2. Optimize image loading
  const optimizeImages = () => {
    const images = document.querySelectorAll("img[data-src]");
    const imageObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const img = entry.target;
          img.src = img.dataset.src;
          img.removeAttribute("data-src");
          imageObserver.unobserve(img);
        }
      });
    });

    images.forEach((img) => imageObserver.observe(img));
  };

  // 3. Minimize main thread blocking
  const minimizeMainThreadBlocking = () => {
    // Use requestIdleCallback for non-critical tasks
    if ("requestIdleCallback" in window) {
      requestIdleCallback(() => {
        // Perform non-critical initialization
        preloadOptimizer.preloadNextSections();
      });
    } else {
      // Fallback for browsers without requestIdleCallback
      setTimeout(() => {
        preloadOptimizer.preloadNextSections();
      }, 100);
    }
  };

  deferNonCriticalJS();
  optimizeImages();
  minimizeMainThreadBlocking();
};
